import React, { useState } from 'react';
import {
  ShieldCheck,
  Crop,
  Sun,
  Flame,
  UserCheck,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  FileSearch,
  Sliders,
  Feather,
  Edit3,
  Check,
  Plus,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { PhotoAnalysis, PhotoMediumType, PhotoSourceType, SubjectIdentity, QueueItemStatus } from '../types';
import { MEDIUM_LABELS, SOURCE_TYPE_LABELS } from '../services/geminiService';
import { SubjectMap, FaceThumbnail, hasUsableBounds } from './SubjectMap';
import { createFaceThumbnail } from '../services/imageUtils';

interface AnalysisCardProps {
  analysis: PhotoAnalysis;
  status?: QueueItemStatus;
  onMediumChange?: (newMedium: PhotoMediumType) => void;
  onSubjectUpdate?: (subjectIndex: number, updatedFields: Partial<SubjectIdentity>) => void;
  onAddSubject?: () => void;
  onAddSubjectManual?: () => void;
  /** Locate an already-identified person who lacks faceBounds. */
  onLocateSubject?: (subjectIndex: number) => void;
  locatingPersonIndex?: number | null;
  onCancelSubjectPlacement?: () => void;
  onPlaceSubjectFace?: (nx: number, ny: number) => void;
  subjectPlacementMode?: boolean;
  onRemoveSubject?: (subjectIndex: number) => void;
  hasRestoredVersions?: boolean;
  analysisDirty?: boolean;
  onRerenderWithUpdatedSettings?: () => void;
  /** Same preview image used for forensic analysis (for Subject Map coordinates). */
  analysisImageUrl?: string;
  isProcessing?: boolean;
}

const GENDER_OPTIONS: Array<{
  value: SubjectIdentity['perceivedGender'];
  label: string;
  badgeStyle: string;
}> = [
  { value: 'female', label: 'Female / Woman', badgeStyle: 'bg-pink-50 text-pink-800 border-pink-200' },
  { value: 'male', label: 'Male / Man', badgeStyle: 'bg-blue-50 text-blue-800 border-blue-200' },
  { value: 'child-female', label: 'Girl / Child (F)', badgeStyle: 'bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200' },
  { value: 'child-male', label: 'Boy / Child (M)', badgeStyle: 'bg-cyan-50 text-cyan-800 border-cyan-200' },
  { value: 'unspecified', label: 'Unspecified', badgeStyle: 'bg-slate-100 text-slate-700 border-slate-200' },
];

const AGE_PRESETS = [
  'Infant / Toddler (0–3)',
  'Child (4–10)',
  'Teenager (11–17)',
  'Young Adult (18–30)',
  'Adult (30–50)',
  'Middle-Aged (50–65)',
  'Elderly / Senior (65+)',
];

export const AnalysisCard: React.FC<AnalysisCardProps> = ({
  analysis,
  status = 'ready_for_review',
  onMediumChange,
  onSubjectUpdate,
  onAddSubject,
  onAddSubjectManual,
  onLocateSubject,
  locatingPersonIndex = null,
  onCancelSubjectPlacement,
  onPlaceSubjectFace,
  subjectPlacementMode = false,
  onRemoveSubject,
  hasRestoredVersions = false,
  analysisDirty = false,
  onRerenderWithUpdatedSettings,
  analysisImageUrl,
  isProcessing = false,
}) => {
  const [expanded, setExpanded] = useState<boolean>(true);
  const [isChangingMedium, setIsChangingMedium] = useState<boolean>(false);
  const [editingSubjectIndex, setEditingSubjectIndex] = useState<number | null>(null);
  const [selectedSubjectIndex, setSelectedSubjectIndex] = useState<number | null>(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showAllLocations, setShowAllLocations] = useState(false);

  const createThumb = React.useCallback(
    (url: string, bounds: NonNullable<SubjectIdentity['faceBounds']>) =>
      createFaceThumbnail(url, bounds, { padding: 0.12, size: 96 }),
    []
  );

  const visibleCount = analysis.visiblePersonCount ?? analysis.subjects?.length ?? 0;
  const mappedCount =
    analysis.mappedFaceCount ??
    (analysis.subjects || []).filter((s) => s.faceBounds && s.faceBoundsSource !== 'legacy').length;
  const unmappedCount = analysis.unmappedSubjectCount ?? Math.max(0, visibleCount - mappedCount);
  const showCoverageWarning = visibleCount > 0 && unmappedCount > 0;

  const getMediumBadgeStyle = (type: PhotoMediumType) => {
    switch (type) {
      case 'digital-photo':
        return 'bg-emerald-50 text-emerald-800 border-emerald-200';
      case 'sepia-albumen':
        return 'bg-teal-50 text-teal-800 border-teal-200';
      case 'silver-gelatin-bw':
        return 'bg-slate-100 text-slate-800 border-slate-300';
      case 'kodachrome-color':
        return 'bg-red-50 text-red-800 border-red-200';
      case 'polaroid-instant':
        return 'bg-teal-50 text-teal-800 border-teal-200';
      case 'aged-c41-color':
        return 'bg-amber-50 text-amber-900 border-amber-200';
      case 'hand-tinted-portrait':
        return 'bg-purple-50 text-purple-800 border-purple-200';
      case 'cyanotype-monochrome':
        return 'bg-sky-50 text-sky-800 border-sky-200';
      case 'platinum-palladium':
        return 'bg-zinc-100 text-zinc-800 border-zinc-300';
      case 'daguerreotype-tintype':
        return 'bg-slate-100 text-slate-800 border-slate-300';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getGenderBadge = (gender: SubjectIdentity['perceivedGender']) => {
    const opt = GENDER_OPTIONS.find((g) => g.value === gender);
    return opt ? opt.badgeStyle : 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const getGenderLabel = (gender: SubjectIdentity['perceivedGender']) => {
    const opt = GENDER_OPTIONS.find((g) => g.value === gender);
    return opt ? opt.label : gender;
  };

  const currentLabel =
    analysis.photoTypeLabel || MEDIUM_LABELS[analysis.photoType] || analysis.photoType;
  const sourceLabel =
    analysis.sourceTypeLabel ||
    (analysis.sourceType ? SOURCE_TYPE_LABELS[analysis.sourceType as PhotoSourceType] : null) ||
    null;
  const isDigital =
    analysis.photoType === 'digital-photo' || analysis.sourceType === 'native-digital';
  const captureIssues = analysis.captureIssues || [];

  const handleGenderChange = (
    index: number,
    newGender: SubjectIdentity['perceivedGender']
  ) => {
    if (!onSubjectUpdate) return;

    let updatedSafeguard = '';
    let updatedSoftness = '';

    if (newGender === 'female' || newGender === 'child-female') {
      updatedSafeguard =
        'NEVER alter gender. Retain natural female facial anatomy; do NOT square jaw, add stubble, or masculinize features.';
      updatedSoftness =
        'Preserve soft feminine skin transitions and authentic period hairstyle. Do not etch deep smile lines or wrinkles.';
    } else if (newGender === 'male' || newGender === 'child-male') {
      updatedSafeguard =
        'Retain true masculine facial structure, chin, hairline, and masculine grooming without distorting likeness.';
      updatedSoftness = 'Maintain natural skin tones without harsh over-carved crease lines.';
    } else {
      updatedSafeguard =
        'Strictly preserve original facial likeness and structure without artificial gender bias or smoothing.';
      updatedSoftness = 'Maintain smooth analog optical softness.';
    }

    onSubjectUpdate(index, {
      perceivedGender: newGender,
      genderSafeguards: updatedSafeguard,
      facialSoftnessGuidance: updatedSoftness,
      isUserOverridden: true,
    });
  };

  const handleAgeChange = (index: number, newAge: string) => {
    if (!onSubjectUpdate) return;
    onSubjectUpdate(index, {
      apparentAge: newAge,
      isUserOverridden: true,
    });
  };

  const selectSubject = (index: number) => {
    setSelectedSubjectIndex(index);
    setEditingSubjectIndex(index);
    setExpanded(true);
  };

  return (
    <div className="kin-studio-panel overflow-hidden shadow-sm">
      <div
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200 cursor-pointer hover:bg-slate-100/80 transition-colors"
      >
        <div className="flex items-center space-x-2.5">
          <div className="p-1.5 bg-teal-50 border border-teal-200 rounded-lg text-teal-700">
            <FileSearch className="w-4 h-4" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-sm font-semibold text-slate-900">
                Forensic Pre-Restoration Analysis
              </h3>
              {sourceLabel && (
                <span className="text-[11px] font-semibold px-2 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200">
                  {sourceLabel}
                </span>
              )}
              <span
                className={`text-[11px] font-semibold px-2 py-0.5 rounded border ${getMediumBadgeStyle(
                  analysis.photoType
                )}`}
              >
                {currentLabel}
              </span>
              {analysis.estimatedEra && (
                <span className="text-[10px] text-slate-500 font-mono">• {analysis.estimatedEra}</span>
              )}
            </div>
            <p className="text-xs text-slate-600 line-clamp-1 mt-0.5">{analysis.summary}</p>
          </div>
        </div>

        <button type="button" className="text-slate-500 hover:text-slate-800 p-1">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {expanded && (
        <div className="p-4 space-y-4 text-xs text-slate-700">
          {analysisDirty && hasRestoredVersions && (
            <div className="kin-analysis-dirty">
              <p>
                <strong>Subject settings changed.</strong> The displayed restoration was generated
                using previous identity settings. Re-render to apply the updated guidance.
              </p>
              {onRerenderWithUpdatedSettings && (
                <button
                  type="button"
                  className="kin-btn kin-btn-primary text-xs py-1.5 px-3"
                  disabled={isProcessing || status === 'restoring'}
                  onClick={onRerenderWithUpdatedSettings}
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Re-render with Updated Settings
                </button>
              )}
            </div>
          )}

          <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Source
                </div>
                <div className="text-xs font-semibold text-slate-800 mt-0.5">
                  {sourceLabel || 'Unknown Source'}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Medium
                </div>
                <div className="text-xs font-semibold text-teal-800 mt-0.5">{currentLabel}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                  Era
                </div>
                <div className="text-xs font-semibold text-slate-800 mt-0.5">
                  {analysis.estimatedEra || 'Unspecified'}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-2 flex-wrap pt-1 border-t border-slate-200">
              <span className="font-semibold text-slate-800 text-xs flex items-center gap-1.5">
                <Sliders className="w-3.5 h-3.5 text-teal-600" />
                Photographic Medium:
                <span className="text-teal-700 font-bold">{currentLabel}</span>
              </span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsChangingMedium(!isChangingMedium);
                }}
                className="text-[11px] text-teal-700 hover:text-teal-800 underline font-medium"
              >
                {isChangingMedium ? 'Close medium selector' : 'Change medium'}
              </button>
            </div>

            {isChangingMedium && (
              <div className="pt-2 border-t border-slate-200 space-y-1.5">
                <p className="text-[11px] text-slate-600">
                  {isDigital
                    ? 'Select the photographic medium. Use Native Digital Photograph for phone/camera originals; choose a historical medium only when the image contains a physical print.'
                    : 'Select the true historical photographic process to guide color restoration accurately:'}
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 pt-1">
                  {(Object.keys(MEDIUM_LABELS) as PhotoMediumType[]).map((key) => {
                    const isSelected = analysis.photoType === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => {
                          if (onMediumChange) onMediumChange(key);
                          setIsChangingMedium(false);
                        }}
                        className={`text-left p-2 rounded-lg text-[11px] font-medium border transition-colors ${
                          isSelected
                            ? 'bg-teal-600 text-white font-bold border-teal-500 shadow'
                            : 'bg-white border-slate-200 text-slate-700 hover:border-teal-500 hover:text-teal-800'
                        }`}
                      >
                        {MEDIUM_LABELS[key]}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            {[
              {
                icon: <Crop className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />,
                title: 'Perspective & Crop',
                body:
                  analysis.hasPerspectiveDistortion || analysis.hasBackgroundClutter
                    ? 'Angle skew & border clutter detected'
                    : 'Frame clean, aligned flat',
              },
              {
                icon: <Sun className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />,
                title: isDigital ? 'Glare / Flare' : 'Glare & Reflections',
                body: analysis.hasGlareOrReflections || captureIssues.some((c) => /flare|glare/i.test(c))
                  ? isDigital
                    ? 'Lens flare / highlight wash detected'
                    : 'Flash reflection / room glare detected'
                  : 'Minimal capture reflection',
              },
              {
                icon: <Flame className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />,
                title: isDigital ? 'Capture Issues' : 'Physical Defects',
                body: isDigital
                  ? captureIssues.length > 0
                    ? captureIssues.join(', ')
                    : analysis.damageTypes.length > 0
                      ? analysis.damageTypes.join(', ')
                      : 'No major capture defects flagged'
                  : analysis.damageTypes.length > 0
                    ? analysis.damageTypes.join(', ')
                    : 'Minimal surface damage',
              },
            ].map((card) => (
              <div
                key={card.title}
                className="p-2.5 rounded-lg bg-slate-50 border border-slate-200 flex items-start space-x-2"
              >
                {card.icon}
                <div>
                  <span className="font-medium text-slate-800 block">{card.title}</span>
                  <span className="text-[11px] text-slate-600">{card.body}</span>
                </div>
              </div>
            ))}
          </div>

          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg flex items-start space-x-2.5">
            <Feather className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-teal-800 text-xs">
                {isDigital
                  ? 'Identity & Detail Preservation'
                  : 'Analog Skin Softness Directive (No Deep Creases)'}
              </div>
              <p className="text-[11.5px] text-slate-600 mt-0.5 leading-relaxed">
                {isDigital
                  ? analysis.facialTextureNotes ||
                    'Preserve original digital-camera character. Correct only supported capture defects. Do not invent facial micro-detail or fake sharpness.'
                  : analysis.facialTextureNotes ||
                    'Preserve natural film lens softness and smooth skin gradients. AI will not carve deep smile creases, exaggerated wrinkles, or hollow cheek lines.'}
              </p>
            </div>
          </div>

          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-slate-700 font-semibold uppercase tracking-wider text-[11px] flex items-center gap-1.5">
                  <UserCheck className="w-4 h-4 text-teal-600" />
                  People in this photo
                </span>
                <p className="text-[10px] text-slate-600">
                  Face thumbnails identify each person. Adjust gender or age
                  {hasRestoredVersions
                    ? ', then re-render with updated settings.'
                    : ' before restoring.'}
                </p>
              </div>

              {(onAddSubject || onAddSubjectManual) && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowAddMenu((v) => !v)}
                    className="inline-flex items-center space-x-1 px-2.5 py-1 text-[11px] bg-white hover:bg-slate-50 text-teal-700 rounded-lg border border-slate-200 transition-colors"
                    title="Add a person Kinvera missed entirely"
                  >
                    <Plus className="w-3 h-3" />
                    <span>Add Person</span>
                  </button>
                  {showAddMenu && (
                    <div className="absolute right-0 z-20 mt-1 w-60 rounded-lg border border-slate-200 bg-white shadow-lg py-1 text-[11px]">
                      <p className="px-3 py-1.5 text-slate-500 border-b border-slate-100">
                        Only if someone is missing from the list
                      </p>
                      {(onAddSubjectManual || onAddSubject) && (
                        <button
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 text-slate-800"
                          onClick={() => {
                            setShowAddMenu(false);
                            (onAddSubjectManual || onAddSubject)?.();
                          }}
                        >
                          Add missing person
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {showCoverageWarning && (
              <div className="p-2.5 rounded-lg border border-amber-200 bg-amber-50 text-[11px] text-amber-950 leading-relaxed">
                <strong>
                  {visibleCount} people identified · {mappedCount} automatically located
                </strong>
                <span className="block mt-0.5 text-amber-900/90">
                  {unmappedCount === 1
                    ? 'One identified person could not be positioned automatically. Locate that person below if needed.'
                    : `${unmappedCount} identified people could not be positioned automatically. Locate them below if needed.`}
                </span>
              </div>
            )}

            {(analysisImageUrl &&
              ((analysis.subjects && analysis.subjects.length > 0) || subjectPlacementMode)) && (
              <SubjectMap
                imageUrl={analysisImageUrl}
                subjects={analysis.subjects || []}
                selectedIndex={selectedSubjectIndex}
                onSelect={selectSubject}
                placementMode={subjectPlacementMode}
                onPlaceFace={onPlaceSubjectFace}
                onCancelPlacement={onCancelSubjectPlacement}
                locatingPersonIndex={locatingPersonIndex}
                showAllLocations={showAllLocations}
                onToggleShowAllLocations={setShowAllLocations}
              />
            )}

            {(!analysis.subjects || analysis.subjects.length === 0) && !subjectPlacementMode && (
              <div className="p-3 bg-slate-50 border border-dashed border-slate-300 rounded-lg text-center text-slate-600 text-xs">
                <span>No distinct people identified automatically.</span>
                {(onAddSubjectManual || onAddSubject) && (
                  <button
                    type="button"
                    onClick={() => (onAddSubjectManual || onAddSubject)?.()}
                    className="block mx-auto mt-1 text-teal-700 underline font-medium"
                  >
                    Add a person manually
                  </button>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {analysis.subjects?.map((subj) => {
                const isEditing = editingSubjectIndex === subj.index;
                const isSelected = selectedSubjectIndex === subj.index;
                const located = hasUsableBounds(subj);

                return (
                  <div
                    key={subj.index}
                    id={`kin-subject-${subj.index}`}
                    onClick={() => setSelectedSubjectIndex(subj.index)}
                    className={`p-3 rounded-xl border transition-all cursor-pointer ${
                      subj.isUserOverridden
                        ? 'bg-teal-50/70 border-teal-300 shadow-sm'
                        : isSelected
                          ? 'bg-white border-teal-400 ring-1 ring-teal-200'
                          : 'bg-white border-slate-200'
                    }`}
                  >
                    <div className="flex items-start gap-2.5">
                      {analysisImageUrl ? (
                        <FaceThumbnail
                          imageUrl={analysisImageUrl}
                          subject={subj}
                          createThumb={createThumb}
                        />
                      ) : (
                        <div className="kin-face-thumb kin-face-thumb-empty" aria-hidden>
                          ?
                        </div>
                      )}

                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900 text-xs">
                            Person #{subj.index}
                          </span>
                          <div className="flex items-center gap-1 flex-wrap justify-end">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditingSubjectIndex(isEditing ? null : subj.index);
                                setSelectedSubjectIndex(subj.index);
                              }}
                              className={`inline-flex items-center space-x-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                                isEditing
                                  ? 'bg-teal-600 text-white'
                                  : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200'
                              }`}
                            >
                              <Edit3 className="w-3 h-3" />
                              <span>{isEditing ? 'Done' : 'Adjust'}</span>
                            </button>
                            {located && onLocateSubject && analysisImageUrl && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSubjectIndex(subj.index);
                                  onLocateSubject(subj.index);
                                }}
                                className="inline-flex items-center space-x-1 px-2 py-0.5 rounded text-[10px] font-medium bg-slate-50 hover:bg-slate-100 text-slate-700 border border-slate-200"
                                title="Reposition this person’s face marker on the photo"
                              >
                                <span>Adjust location</span>
                              </button>
                            )}
                            {onRemoveSubject && analysis.subjects && analysis.subjects.length > 1 && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onRemoveSubject(subj.index);
                                }}
                                className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Remove this person entry"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span
                            className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border uppercase tracking-wider ${getGenderBadge(
                              subj.perceivedGender
                            )}`}
                          >
                            {getGenderLabel(subj.perceivedGender)}
                          </span>
                          <span className="text-[11px] text-slate-700 font-medium">
                            · {subj.apparentAge}
                          </span>
                          {subj.isUserOverridden && (
                            <span className="text-[9.5px] px-1.5 py-0.5 rounded bg-teal-100 text-teal-800 border border-teal-200">
                              Adjusted
                            </span>
                          )}
                        </div>

                        {(subj.visualLocator || subj.visualDescription) && (
                          <div className="text-[11px] text-slate-600 leading-snug">
                            {subj.visualLocator && (
                              <div className="font-medium text-slate-700">{subj.visualLocator}</div>
                            )}
                            {subj.visualDescription &&
                              subj.visualDescription !== subj.visualLocator && (
                                <div className="text-slate-600 line-clamp-2">{subj.visualDescription}</div>
                              )}
                          </div>
                        )}

                        {!located && (
                          <div className="pt-0.5">
                            <p className="text-[10px] text-amber-800 mb-1">
                              Face not automatically located.
                            </p>
                            {onLocateSubject && analysisImageUrl && (
                              <button
                                type="button"
                                className="inline-flex items-center px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-teal-600 text-white hover:bg-teal-500"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSubjectIndex(subj.index);
                                  onLocateSubject(subj.index);
                                }}
                              >
                                Locate on photo
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {isEditing ? (
                      <div className="mt-3 pt-3 border-t border-slate-200 space-y-3 bg-slate-50 p-3 rounded-lg">
                        <div>
                          <label className="block text-[10px] uppercase font-bold text-teal-800 tracking-wider mb-1.5">
                            Correct Gender / Biological Sex:
                          </label>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                            {GENDER_OPTIONS.map((opt) => {
                              const isSelectedGender = subj.perceivedGender === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleGenderChange(subj.index, opt.value);
                                  }}
                                  className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border text-left transition-all flex items-center justify-between ${
                                    isSelectedGender
                                      ? 'bg-teal-600 text-white border-teal-500 font-bold shadow'
                                      : 'bg-white border-slate-200 text-slate-700 hover:border-teal-400 hover:text-teal-800'
                                  }`}
                                >
                                  <span>{opt.label}</span>
                                  {isSelectedGender && <Check className="w-3.5 h-3.5" />}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        <div>
                          <label className="block text-[10px] uppercase font-bold text-teal-800 tracking-wider mb-1.5">
                            Correct Apparent Age:
                          </label>
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {AGE_PRESETS.map((preset) => (
                              <button
                                key={preset}
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAgeChange(subj.index, preset);
                                }}
                                className={`px-2 py-1 rounded text-[11px] font-medium border transition-colors ${
                                  subj.apparentAge === preset
                                    ? 'bg-teal-600 text-white font-bold border-teal-500'
                                    : 'bg-white border-slate-200 text-slate-600 hover:text-slate-900 hover:border-slate-300'
                                }`}
                              >
                                {preset}
                              </button>
                            ))}
                          </div>

                          <div className="flex items-center space-x-2">
                            <span className="text-[11px] text-slate-600 shrink-0">
                              Custom Age/Description:
                            </span>
                            <input
                              type="text"
                              value={subj.apparentAge}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => handleAgeChange(subj.index, e.target.value)}
                              placeholder='e.g. "Woman in her early 20s", "Elderly grandmother", "Girl age 6"'
                              className="flex-1 bg-white border border-slate-300 rounded-lg px-2.5 py-1 text-xs text-slate-900 focus:outline-none focus:border-teal-500"
                            />
                          </div>
                        </div>

                        <div className="p-2 bg-emerald-50 rounded border border-emerald-200 text-[10.5px] text-emerald-900 flex items-start space-x-1.5">
                          <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5 text-emerald-600" />
                          <span>
                            Fidelity lock updated: Restoration prompt will strictly enforce this
                            subject as <strong>{getGenderLabel(subj.perceivedGender)}</strong> (
                            {subj.apparentAge}) with delicate facial anatomy and no masculine/feminine
                            drift.
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-2 space-y-1 text-[11px] text-slate-600">
                        {subj.facialStructure && (
                          <p className="leading-relaxed">
                            <strong className="text-slate-600 font-medium">Features:</strong>{' '}
                            {subj.facialStructure}
                          </p>
                        )}

                        <div className="text-[10.5px] text-emerald-900 bg-emerald-50 p-2 rounded border border-emerald-200 leading-relaxed">
                          <strong>Fidelity Lock:</strong> {subj.genderSafeguards}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="p-3 bg-teal-50/60 border border-teal-200 rounded-lg flex items-start space-x-2.5">
            <UserCheck className="w-4 h-4 text-teal-700 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-teal-900 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5 text-teal-700" />
                Identity & Facial Fidelity Policy
              </div>
              <p className="text-[11.5px] text-slate-700 mt-0.5 leading-relaxed">
                {analysis.identityNotes} (Guaranteed sex/gender preservation; no AI jaw squaring,
                masculinization, or exaggerated face wrinkles).
              </p>
            </div>
          </div>

          <div>
            <span className="text-slate-500 font-medium uppercase tracking-wider text-[10px] block mb-1.5">
              Prescribed Conservation Actions
            </span>
            <ul className="space-y-1.5">
              {analysis.conservativeRecommendations.map((rec, i) => (
                <li key={i} className="flex items-start space-x-2 text-[11.5px] text-slate-700">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};
